// src/pages/Reports.jsx
import { useEffect, useState } from 'react';
import { api, getTokens } from '../lib/api.js';

function StatCard({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-ink/10 bg-white p-4">
      <div className={`text-2xl font-mono font-semibold ${tone ?? 'text-charcoal'}`}>{value}</div>
      <div className="text-xs text-ink/60 mt-0.5">{label}</div>
    </div>
  );
}

export default function Reports() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api('/reports/summary').then(setData).catch((err) => setError(err.message));
  }, []);

  function download(format) {
    const { accessToken } = getTokens();
    window.open(`/api/reports/export.${format}?access_token=${encodeURIComponent(accessToken)}`, '_blank');
  }

  if (error) return <div className="p-8 text-p1 text-sm">{error}</div>;
  if (!data) return <div className="p-8 text-ink/50 text-sm">Loading…</div>;

  const maxStatusCount = Math.max(1, ...data.caseMetrics.byStatus.map((s) => s.count));

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-charcoal">Reports &amp; Analytics</h1>
        <div className="flex gap-2">
          <button onClick={() => download('csv')} className="text-xs rounded-md border border-ink/20 px-3 py-1.5 hover:bg-ink hover:text-white transition-colors">CSV</button>
          <button onClick={() => download('xlsx')} className="text-xs rounded-md border border-ink/20 px-3 py-1.5 hover:bg-ink hover:text-white transition-colors">Excel</button>
          <button onClick={() => download('pdf')} className="text-xs rounded-md border border-ink/20 px-3 py-1.5 hover:bg-ink hover:text-white transition-colors">PDF</button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-4 gap-4">
        <StatCard label="Total cases" value={data.caseMetrics.total} />
        <StatCard label="Escalated" value={data.responseTime.escalatedCount} />
        <StatCard label="Avg. response" value={data.responseTime.avgResponseSeconds !== null ? `${data.responseTime.avgResponseSeconds}s` : '—'} />
        <StatCard label="SLA compliance" value={data.responseTime.slaComplianceRate !== null ? `${data.responseTime.slaComplianceRate}%` : '—'} tone="text-accent" />
      </div>

      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-3">Cases by status</h2>
        <div className="space-y-2">
          {data.caseMetrics.byStatus.map((row) => (
            <div key={row.status} className="flex items-center gap-3">
              <span className="w-24 text-xs capitalize text-ink/70 shrink-0">{(row.status ?? 'unknown').replace('_', ' ')}</span>
              <div className="flex-1 h-5 bg-ink/5 rounded overflow-hidden">
                <div className="h-full bg-accent rounded" style={{ width: `${(row.count / maxStatusCount) * 100}%` }} />
              </div>
              <span className="w-8 text-xs text-right font-mono text-ink/60">{row.count}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-8">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-ink/50 mb-3">Staff performance</h2>
        <div className="rounded-lg border border-ink/10 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-ink/5 text-left text-xs uppercase tracking-wide text-ink/50">
              <tr><th className="px-4 py-2">Staff</th><th className="px-4 py-2">Cases</th><th className="px-4 py-2">Avg. response</th></tr>
            </thead>
            <tbody>
              {data.staffPerformance.length === 0 && (
                <tr><td colSpan={3} className="px-4 py-4 text-center text-ink/40 text-xs">No cases assigned in this period.</td></tr>
              )}
              {data.staffPerformance.map((s) => (
                <tr key={s.id} className="border-t border-ink/5">
                  <td className="px-4 py-2">{s.name}</td>
                  <td className="px-4 py-2 font-mono">{s.cases_handled}</td>
                  <td className="px-4 py-2 font-mono">{s.avg_response_seconds !== null ? `${s.avg_response_seconds}s` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
